import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import { AppCell, AppFace, elementOf } from '../../components/nucleus/cells';
import { Cell } from '../../components/nucleus/Collection';
import DataTable from '../../components/nucleus/DataTable';
import StatusPill from '../../components/nucleus/StatusPill';
import Spark from '../../components/nucleus/Spark';
import { SHELVES, shelfOf } from '../../components/nucleus/shelves';
import { statusOf, priceOf, shortPrice, asksSummary, ownedWord } from './appsData';
import { ago, dateOf, dayOf, fmt } from '../../nucleus/format';

// THE APPS BOARD, in the shelf's two views. CARDS are the platform's squares
// with only what matters at a glance: the app, when it was last active (in the
// store, its price) under its name, its state on the corner, and Open. ROWS carry
// everything there is to compare, in the one table (DataTable): its realms, when
// it was last used, its operations and plugin runs, what it has been paid, its
// month as a quiet line; in the store its category, people, price, what its
// contract asks and when it was listed. Both open the app.

const hueOf = (app) => elementOf(app)?.hue || app?.brand_color || 'var(--nx-zone-hue, var(--ofself-ink))';
const realmsOf = (row) => (row?.realms?.length ? row.realms : row?.realm ? [row.realm] : []);
const endedOn = (row) => (row?.ep?.expires_at
    ? `Ended ${dateOf(row.ep.expires_at)}`
    : 'Ended');
// an app opens when it is running here and has somewhere to go
const openable = (e) => (e.row && statusOf(e.row) === 'active' && e.app.website_url ? e.app.website_url : null);

/** A day-by-day line, ending today: the shape of its use, not a figure to read (Spark). */
export function Line({ days, hue, tall = false, legend = false }) {
    const n = days.length;
    const back = (i) => {
        const d = new Date();
        d.setDate(d.getDate() - (n - 1 - i));
        return dayOf(d);
    };
    return (
        <Spark values={days} hue={hue} tall={tall}
            readout={legend ? (i) => `${back(i)} · ${fmt(days[i])}` : undefined}
            ends={legend ? [back(0), 'Today'] : undefined} />
    );
}

const pillOf = (status) => (status ? <StatusPill status={status} of="app" /> : null);
// on a connected card only what is wrong earns the corner; Connected goes without saying
const troubleOf = (status) => (status && status !== 'active' ? pillOf(status) : null);

/** Open: the app, in a new tab. A sibling of the card or row, never inside its press. */
function OpenApp({ entry, label = false }) {
    const url = openable(entry);
    if (!url) return null;
    return (
        <a
            className={`apps-open${label ? ' is-label' : ''}`} href={url} target="_blank" rel="noopener noreferrer"
            aria-label={`Open ${entry.app.name}`} title={`Open ${entry.app.name}`} onClick={(ev) => ev.stopPropagation()}
        >
            {label && 'Open'}<ArrowUpRight aria-hidden="true" />
        </a>
    );
}

/** One connected app, as a square: the app, when it was last active, what is wrong if anything, Open. */
export function ConnectedCell({ entry, usage, current, onPress, actions }) {
    const { app, row } = entry;
    const status = row ? statusOf(row) : null;
    const u = usage.of(app.id);
    const note = status === 'expired' ? endedOn(row) : u.last_active ? ago(u.last_active) : 'not used yet';
    return (
        <div className="apps-cellwrap">
            <Cell actions={actions}>
                <AppCell app={app} note={note} badge={troubleOf(status)} current={current} title={app.description || app.name} onPress={onPress} />
            </Cell>
            <OpenApp entry={entry} />
        </div>
    );
}

// when a time was, as a number to sort by (nothing sorts last)
const when = (iso) => (iso ? new Date(iso).getTime() : null);
const amount = (text) => {
    const n = parseFloat(String(text || '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) ? n : null;
};
const face = (app) => <span className="apps-face"><AppFace app={app} size={24} /></span>;
const OPEN_COLUMN = { id: 'open', label: '', lock: true, min: 84, grow: 0, className: 'apps-table__open', cell: (e) => <OpenApp entry={e} label /> };

/** Connected apps as a table: everything there is to compare, the last used first. */
export function AppTable({ id = 'apps-connected', entries, usage, openId, onOpen, actionsOf }) {
    const status = (e) => (e.row ? statusOf(e.row) : null);
    const columns = [
        { id: 'app', label: 'App', lock: true, min: 180, grow: 2, className: 'apps-table__app', value: (e) => e.app.name,
          cell: (e) => <>{face(e.app)}<span className="apps-table__name">{e.app.name}</span>{troubleOf(status(e))}</> },
        { id: 'realms', label: 'Realms', min: 110, grow: 1.2, className: 'apps-table__realms',
          value: (e) => realmsOf(e.row).map((r) => r.name).join(' + ') || null,
          cell: (e) => realmsOf(e.row).map((r) => r.name).join(' + ') || '—' },
        { id: 'last', label: 'Last used', min: 96, grow: 0, asc: false,
          value: (e) => (status(e) === 'expired' ? null : when(usage.of(e.app.id).last_active)),
          cell: (e) => {
              const u = usage.of(e.app.id);
              return status(e) === 'expired' ? endedOn(e.row) : u.last_active ? ago(u.last_active) : '—';
          } },
        { id: 'node_ops', label: 'Node operations', num: true, min: 90, grow: 0, value: (e) => usage.of(e.app.id).node_ops || 0, cell: (e) => fmt(usage.of(e.app.id).node_ops) },
        { id: 'actuator_runs', label: 'Plugin runs', num: true, min: 90, grow: 0, value: (e) => usage.of(e.app.id).actuator_runs || 0, cell: (e) => fmt(usage.of(e.app.id).actuator_runs) },
        { id: 'paid', label: 'Paid so far', num: true, min: 84, grow: 0, value: (e) => amount(usage.of(e.app.id).paid), cell: (e) => usage.of(e.app.id).paid || '—' },
        { id: 'month', label: `Last ${usage.days || 30} days`, min: 110, grow: 1.2,
          cell: (e) => (status(e) !== 'expired' ? <Line days={usage.of(e.app.id).by_day} hue={hueOf(e.app)} /> : null) },
        { id: 'since', label: 'Connected since', min: 100, grow: 0, off: true, asc: false, value: (e) => when(e.row?.ep?.created_at), cell: (e) => dateOf(e.row?.ep?.created_at) || '—' },
        { id: 'sign_ins', label: 'Sign-ins', num: true, min: 72, grow: 0, off: true, value: (e) => usage.of(e.app.id).sign_ins || 0, cell: (e) => fmt(usage.of(e.app.id).sign_ins) },
        OPEN_COLUMN,
    ];
    return (
        <DataTable
            id={id} label="Connected apps" columns={columns} rows={entries} keyOf={(e) => e.app.id}
            sort={{ id: 'last', dir: 'desc' }} currentKey={openId} onOpen={onOpen} actionsOf={actionsOf}
        />
    );
}

/** One app in the store, as a square: the app, its price under its name (or
 *  Connected, when it is), what is wrong on the corner, Open. */
export function StoreCell({ entry, current, onPress, actions }) {
    const { app, row } = entry;
    const status = row ? statusOf(row) : null;
    const price = !app.is_purchased ? priceOf(app) : null;
    const connected = status === 'active' || status === 'paused';
    return (
        <div className="apps-cellwrap">
            <Cell actions={actions}>
                <AppCell
                    app={app} note={connected ? 'Connected' : app.is_purchased ? ownedWord(entry) : price ? shortPrice(price) : 'Free'}
                    badge={troubleOf(status)}
                    current={current} title={app.description || app.name} onPress={onPress}
                />
            </Cell>
            <OpenApp entry={entry} />
        </div>
    );
}

const STATUS_ORDER = { active: 0, paused: 1, expired: 2 };

/** The store as a table: what you could add first (newest first), then what is yours. */
export function StoreTable({ entries, openId, onOpen, actionsOf }) {
    const status = (e) => (e.row ? statusOf(e.row) : null);
    const columns = [
        { id: 'app', label: 'App', lock: true, min: 180, grow: 2, className: 'apps-table__app', value: (e) => e.app.name,
          cell: (e) => <>{face(e.app)}<span className="apps-table__name">{e.app.name}</span></> },
        { id: 'category', label: 'Category', min: 90, grow: 0.8, value: (e) => SHELVES.find((s) => s.id === shelfOf(e.app))?.label || null,
          cell: (e) => SHELVES.find((s) => s.id === shelfOf(e.app))?.label || '—' },
        { id: 'people', label: 'People', num: true, min: 70, grow: 0, value: (e) => e.app.installs ?? null,
          cell: (e) => (e.app.installs == null ? '—' : fmt(e.app.installs)) },
        { id: 'price', label: 'Price', num: true, min: 124, grow: 0, value: (e) => (priceOf(e.app) ? amount(priceOf(e.app)) : 0),
          cell: (e) => {
              const price = priceOf(e.app);
              return price ? shortPrice(price) : 'Free';
          } },
        { id: 'asks', label: 'Its contract asks', min: 120, grow: 1.2, className: 'apps-table__realms',
          value: (e) => { const a = asksSummary(e.app); return a.reads + a.writes; },
          cell: (e) => {
              const asks = asksSummary(e.app);
              return asks.none ? 'Nothing' : [asks.reads && `reads ${asks.reads}`, asks.writes && `writes ${asks.writes}`].filter(Boolean).join(' · ');
          } },
        { id: 'listed', label: 'Listed', min: 96, grow: 0, asc: false, value: (e) => when(e.app.created_at), cell: (e) => (e.app.created_at ? dateOf(e.app.created_at) : '—') },
        // what you have already: its contract's state if connected, else bought or
        // subscribed without being connected; not yours sorts first
        { id: 'here', label: 'Here', min: 110, grow: 0, asc: true,
          value: (e) => (ownedWord(e) ? 1 + (STATUS_ORDER[status(e)] ?? 3) : 0),
          cell: (e) => (status(e) ? pillOf(status(e)) : ownedWord(e) ? <span className="apps-owned">{ownedWord(e)}</span> : '—') },
        OPEN_COLUMN,
    ];
    return (
        <DataTable
            id="apps-store" label="App store" columns={columns} rows={entries} keyOf={(e) => e.app.id}
            sort={{ id: 'here', dir: 'asc' }} currentKey={openId} onOpen={onOpen} actionsOf={actionsOf}
        />
    );
}
